import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import { formatLatexForDisplay } from "../lib/latexUtils";

const statusOptions = ["all", "correct", "incomplete", "needs_review", "needs_improvement"];
const sortOptions = [
  { value: "recent", label: "Most Recent" },
  { value: "oldest", label: "Oldest" }
];

function formatAttemptDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function getStatusLabel(status) {
  if (status === "correct") return "Correct";
  if (status === "incomplete") return "Incomplete";
  if (status === "needs_improvement") return "Needs Improvement";
  return "Needs Review";
}

function getStatusClass(status) {
  if (status === "correct") return "status-correct";
  if (status === "incomplete") return "status-incomplete";
  if (status === "needs_improvement") return "status-incorrect";
  return "status-missing";
}

function FrameAttempts({ apiBase, token, onPageBusyChange }) {
  const { chapterId, frameId } = useParams();
  const location = useLocation();
  const [conceptTitle, setConceptTitle] = useState(chapterId);
  const [frameTitle, setFrameTitle] = useState("");
  const [frameDifficulty, setFrameDifficulty] = useState("");
  const [frameOrderValue, setFrameOrderValue] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("frame-attempts", loading, "Loading attempt history...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("frame-attempts", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    let mounted = true;

    async function loadChapters() {
      if (!token) return;
      try {
        const response = await fetch(`${apiBase}/api/chapters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const payload = await response.json();
        const found = (payload.chapters || []).find((item) => item.id === chapterId);
        if (mounted && found?.title) setConceptTitle(found.title);
      } catch (err) {
        // ignore
      }
    }

    loadChapters();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId]);

  useEffect(() => {
    let mounted = true;

    async function loadFrameMetadata() {
      if (!token) return;
      try {
        const response = await fetch(`${apiBase}/api/mastery/chapters/${chapterId}/frames`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const payload = await response.json();
        if (!mounted) return;
        const found = (payload.frames || []).find((item) => String(item.frameId) === String(frameId));
        if (found) {
          if (found.frameTitle) setFrameTitle(found.frameTitle);
          if (found.difficulty) setFrameDifficulty(found.difficulty);
          if (Number.isFinite(Number(found.frameOrder))) {
            setFrameOrderValue(Number(found.frameOrder));
          }
        }
      } catch (err) {
        // ignore metadata fetch errors
      }
    }

    loadFrameMetadata();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId, frameId]);

  useEffect(() => {
    let mounted = true;

    async function loadAttempts() {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          chapterId,
          frameId,
          sort: sortBy,
          limit: "50"
        });
        if (statusFilter !== "all") params.set("status", statusFilter);

        const response = await fetch(`${apiBase}/api/attempts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load attempts");
        }
        const payload = await response.json();
        if (!mounted) return;
        const rows = payload.attempts || [];
        setAttempts(rows);
        if (rows[0]?.frameTitle) {
          setFrameTitle(rows[0].frameTitle);
        }
        if (rows[0]?.difficulty) {
          setFrameDifficulty(rows[0].difficulty);
        }
        if (Number.isFinite(Number(rows[0]?.frameOrder))) {
          setFrameOrderValue(Number(rows[0].frameOrder));
        }
      } catch (err) {
        if (mounted) setError(err.message || "Failed to load attempts");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAttempts();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId, frameId, statusFilter, sortBy]);

  const attemptsCount = useMemo(() => attempts.length, [attempts]);
  const practiceParams = useMemo(() => {
    const params = new URLSearchParams();
    if (frameDifficulty) params.set("difficulty", frameDifficulty);
    if (Number.isFinite(Number(frameOrderValue))) {
      params.set("frameOrder", String(Number(frameOrderValue)));
    }
    return params.toString();
  }, [frameDifficulty, frameOrderValue]);
  const practiceHref = `/practice/${chapterId}${practiceParams ? `?${practiceParams}` : ""}`;

  return (
    <section className="page">
      <div className="chapter-home-header">
        <div>
          <div className="breadcrumb">Home / {conceptTitle} / {frameTitle || `Frame ${frameId}`}</div>
          <div className="frame-attempts-title-row">
            <Link to={`/chapters/${chapterId}`} className="back-link-simple" title="Back to Frames" aria-label="Back to Frames">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </Link>
            <h2 className="page-title">{frameTitle || "Frame Attempts"}</h2>
            {frameDifficulty && (
              <span className={`frame-diff-pill ${frameDifficulty === "HOT" ? "diff-hot" : frameDifficulty === "MOT" ? "diff-mot" : "diff-lot"}`}>
                {frameDifficulty}
              </span>
            )}
          </div>
        </div>
        <div className="frame-detail-actions">
          <Link to={practiceHref} className="frame-cta-practice-slate">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            <span>Practice Frame</span>
          </Link>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filter-group">
          <div className="filter-label">Status</div>
          <div className="filter-row">
            {statusOptions.map((item) => (
              <button
                key={item}
                type="button"
                className={`filter-pill ${statusFilter === item ? "active" : ""}`}
                onClick={() => setStatusFilter(item)}
              >
                {item === "all" ? "All" : getStatusLabel(item)}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group align-end">
          <div className="filter-label">Sort</div>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="question-block">
        <div className="question-title-row">
          <div className="question-meta">Attempt History</div>
          <div className="question-meta">{attemptsCount} attempts</div>
        </div>
        {loading && <div className="question-meta">Loading attempts...</div>}
        {error && <div className="error-text">{error}</div>}
        {!loading && !error && attempts.length === 0 && (
          <div className="question-meta">No attempts found for this frame.</div>
        )}
        <div className="attempt-grid">
          {attempts.map((attempt) => (
            <Link
              key={attempt.id}
              to={`/attempts/${attempt.id}`}
              state={{ backTo: `${location.pathname}${location.search || ""}` }}
              className="attempt-card"
            >
              <div className="attempt-card-header">
                <div className="attempt-title">{attempt.frameTitle || frameTitle || "Untitled Frame"}</div>
                <span className={`attempt-badge ${getStatusClass(attempt.status)}`}>
                  {getStatusLabel(attempt.status)}
                </span>
              </div>
              {attempt.questionLatex ? (
                <div className="attempt-latex">
                  <BlockMath math={formatLatexForDisplay(attempt.questionLatex)} />
                </div>
              ) : (
                <div className="question-meta">No question text</div>
              )}
              <div className="attempt-card-footer">
                <span>{attempt.difficulty}</span>
                <span>{formatAttemptDate(attempt.createdAt)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FrameAttempts;
